import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Separator } from "../components/ui/separator";
import { ArrowLeft, Eye, EyeOff, Key } from "lucide-react";
import { toast } from "sonner";
import { MultiSelect } from "../components/ui/multi-select";
import { createUser, getUserDataById, updateUser } from "../services/crmServices/userService";
import { fetchDesks } from "../services/crmServices/deskService";
import { useAuth } from "../hooks/useAuth";
import { UpdatePasswordModal } from "../components/staff/UpdatePasswordModal";
import { fetchPositions } from "../services/crmServices/positionService";

export function CreateStaff() {
    const navigate = useNavigate();
    const { user, updateProfile } = useAuth();
    const params = useParams();
    const staffId = params._id;
    const [allDesks, setAllDesks] = useState([]);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [currentStaff, setCurrentStaff] = useState(null);
    const [positions, setPositions] = useState([]);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        userName: "",
        email: "",
        position: "",
        viewLeadsScope: "Assigned",
        viewLeadsAllowedDesks: [],
        managerDesks: [],
        agentDesk: null,
        agentLeads: [],
        canFastLogin: false,
        phone: "",
        commpeak: "",
        voiso: "",
        coperato: "",
        squaretalk: "",
        octella: "",
        ccc: "",
        commentColor: "#000000",
        password: "",
        permissions: {
            leads: {
                view: false,
                create: false,
                update: false,
                delete: false,
                viewEmail: false,
                viewPhone: false,
                viewActionHistory: false
            },
            staff: {
                view: false,
                create: false,
                update: false,
                delete: false
            },
            desks: {
                view: false,
                create: false,
                update: false,
                delete: false
            },
            gridLeadsColumns: {
                source: false,
                affiliate: false,
                manager: false,
                registered: false,
                modified: false,
                desk: false,
                lastComment: false,
                lastCommDate: false,
                lastCommManager: false,
                age: false
            },
            editLeadFields: {
                firstName: false,
                lastName: false,
                userName: false,
                email: false,
                phone: false,
                secondPhone: false,
                status: false,
                country: false,
                city: false,
                address: false,
                source: false,
                extra: false
            },
            voip: {
                commpeak: false,
                voiso: false,
                coperato: false,
                squaretalk: false,
                octella: false,
                ccc: false
            },
            games: {
                access: false,
                multiplayer: true,
                dailyMinutesLimit: 0
            }
        }
    });
    const [showPassword, setShowPassword] = useState(false);

    // const positions = ["Admin", "Manager", "Sales", "Retention"];
    const viewLeadsOptions = ["Assigned", "Desk", "Floor", "All"];

    useEffect(() => {
        if (user.position.role === "agent") {
            navigate("/");
        }
        fetchDesks().then((res) => {
            if (res?.data?.length) {
                setAllDesks(res.data);
            }
        });
        fetchPositions().then((res) => {
            if (res?.data?.length) {
                setPositions(res.data);
            }
        });
        if (staffId) {
            getUserDataById(staffId).then((response) => {
                if (response.data._id) {
                    const staff = response.data;
                    setCurrentStaff(staff);
                    setFormData((prev) => ({
                        firstName: staff.firstName || "",
                        lastName: staff.lastName || "",
                        userName: staff.userName || "",
                        email: staff.email || "",
                        position: staff.position?._id || "",
                        viewLeadsScope: staff.viewLeadsScope || "Assigned",
                        viewLeadsAllowedDesks: staff.viewLeadsAllowedDesks || [],
                        agentDesk: staff.agentDesk || null,
                        canFastLogin: staff.canFastLogin || false,
                        phone: staff.phone || "",
                        commpeak: staff.commpeak || "",
                        voiso: staff.voiso || "",
                        coperato: staff.coperato || "",
                        squaretalk: staff.squaretalk || "",
                        octella: staff.octella || "",
                        ccc: staff.ccc || "",
                        commentColor: staff.commentColor || "#000000",
                        permissions: {
                            leads: {
                                view: staff.permissions?.leads?.view || false,
                                create: staff.permissions?.leads?.create || false,
                                update: staff.permissions?.leads?.update || false,
                                delete: staff.permissions?.leads?.delete || false,
                                viewEmail: staff.permissions?.leads?.viewEmail || false,
                                viewPhone: staff.permissions?.leads?.viewPhone || false,
                                viewActionHistory: staff.permissions?.leads?.viewActionHistory || false
                            },
                            staff: {
                                view: staff.permissions?.staff?.view || false,
                                create: staff.permissions?.staff?.create || false,
                                update: staff.permissions?.staff?.update || false,
                                delete: staff.permissions?.staff?.delete || false
                            },
                            desks: {
                                view: staff.permissions?.desks?.view || false,
                                create: staff.permissions?.desks?.create || false,
                                update: staff.permissions?.desks?.update || false,
                                delete: staff.permissions?.desks?.delete || false
                            },
                            gridLeadsColumns: {
                                source: staff.permissions?.gridLeadsColumns?.source || false,
                                affiliate: staff.permissions?.gridLeadsColumns?.affiliate || false,
                                manager: staff.permissions?.gridLeadsColumns?.manager || false,
                                registered: staff.permissions?.gridLeadsColumns?.registered || false,
                                modified: staff.permissions?.gridLeadsColumns?.modified || false,
                                desk: staff.permissions?.gridLeadsColumns?.desk || false,
                                lastComment: staff.permissions?.gridLeadsColumns?.lastComment || false,
                                lastCommDate: staff.permissions?.gridLeadsColumns?.lastCommDate || false,
                                lastCommManager: staff.permissions?.gridLeadsColumns?.lastCommManager || false,
                                age: staff.permissions?.gridLeadsColumns?.age || false
                            },
                            editLeadFields: {
                                firstName: staff.permissions?.editLeadFields?.firstName || false,
                                lastName: staff.permissions?.editLeadFields?.lastName || false,
                                userName: staff.permissions?.editLeadFields?.userName || false,
                                email: staff.permissions?.editLeadFields?.email || false,
                                phone: staff.permissions?.editLeadFields?.phone || false,
                                secondPhone: staff.permissions?.editLeadFields?.secondPhone || false,
                                status: staff.permissions?.editLeadFields?.status || false,
                                country: staff.permissions?.editLeadFields?.country || false,
                                city: staff.permissions?.editLeadFields?.city || false,
                                address: staff.permissions?.editLeadFields?.address || false,
                                source: staff.permissions?.editLeadFields?.source || false,
                                extra: staff.permissions?.editLeadFields?.extra || false
                            },
                            voip: {
                                commpeak: staff.permissions?.voip?.commpeak || false,
                                voiso: staff.permissions?.voip?.voiso || false,
                                coperato: staff.permissions?.voip?.coperato || false,
                                squaretalk: staff.permissions?.voip?.squaretalk || false,
                                octella: staff.permissions?.voip?.octella || false,
                                ccc: staff.permissions?.voip?.ccc || false
                            },
                            games: {
                                access: !!staff.permissions?.games?.access,
                                multiplayer: staff.permissions?.games?.multiplayer !== false,
                                dailyMinutesLimit: staff.permissions?.games?.dailyMinutesLimit || 0
                            }
                        }
                    }));
                }
            });
        }
    }, [staffId]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (
            !formData.firstName ||
            !formData.email ||
            !formData.position ||
            !formData.userName ||
            (!formData.password && !staffId)
        ) {
            toast.error("Please fill in all required fields");
            return;
        }
        if (staffId) {
            updateUser({ _id: staffId, ...formData }).then((response) => {
                if (response?.data.RESULT === "ERROR") {
                    toast.error(response.data.message);
                } else {
                    if (staffId === user._id) {
                        updateProfile({ ...user, ...formData });
                    }

                    toast.success(response.data.message);
                    navigate("/staff");
                }
            });
        } else {
            createUser(formData).then((response) => {
                if (response?.data.RESULT === "ERROR") {
                    toast.error(response.data.message);
                } else {
                    toast.success(response.data.message);
                    navigate("/staff");
                }
            });
        }
    };

    const handlePermissionChange = (category, permission, checked) => {
        setFormData((prev) => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [category]: {
                    ...prev.permissions[category],
                    [permission]: checked
                }
            }
        }));
    };

    const handlePositionChange = (value) => {
        if (value !== formData.position) {
            let permissions = {};
            const positionDetails = positions.find((pos) => pos._id === value);
            if (positionDetails) {
                permissions = positionDetails.permissions;
            }

            setFormData((prev) => ({
                ...prev,
                position: value,
                managerDesks: [],
                agentLeads: [],
                permissions: {
                    ...permissions,
                    voip: permissions?.voip || {
                        commpeak: false,
                        voiso: false,
                        coperato: false,
                        squaretalk: false,
                        octella: false,
                        ccc: false
                    },
                    games: permissions?.games || {
                        access: false,
                        multiplayer: true,
                        dailyMinutesLimit: 0
                    }
                },
                viewLeadsScope: value === "Admin" ? "All" : "Assigned"
            }));
        }
    };

    const handleViewLeadsScopeChange = (value) => {
        if (value !== formData.viewLeadsScope) {
            setFormData((prev) => ({ ...prev, viewLeadsScope: value, viewLeadsAllowedDesks: [] }));
        }
    };

    const handlePasswordUpdateSuccess = () => {
        setShowPasswordModal(false);
        toast.success("Password updated successfully!");
    };

    const handleBack = () => {
        navigate("/staff");
    };

    // Check if current staff is an agent (not Admin or Manager)
    const isAgent = currentStaff && currentStaff.position.role === "agent";

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">{staffId ? "Edit" : "Create"} Staff</h1>

                    {/* Action buttons for editing */}
                    {staffId && currentStaff && (
                        <div className="flex space-x-3">

                            {/* Update Password button */}
                            <Button
                                onClick={() => setShowPasswordModal(true)}
                                variant="outline"
                                className="bg-purple-600 text-white hover:bg-purple-700"
                            >
                                <Key className="h-4 w-4 mr-2" />
                                Update Password
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Basic Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="email">Email Address *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="userName">User Name *</Label>
                                <Input
                                    id="userName"
                                    value={formData.userName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, userName: e.target.value }))}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="firstName">First Name *</Label>
                                <Input
                                    id="firstName"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="lastName">Last Name</Label>
                                <Input
                                    id="lastName"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                                    required
                                />
                            </div>

                            <div>
                                <Label htmlFor="commpeak">Commpeak ID</Label>
                                <Input
                                    id="commpeak"
                                    value={formData.commpeak}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, commpeak: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label htmlFor="voiso">Voiso ID</Label>
                                <Input
                                    id="voiso"
                                    value={formData.voiso}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, voiso: e.target.value }))}
                                />
                            </div>

                            <div>
                                <Label htmlFor="coperato">Coperato ID</Label>
                                <Input
                                    id="coperato"
                                    value={formData.coperato}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, coperato: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label htmlFor="squaretalk">SquareTalk ID</Label>
                                <Input
                                    id="squaretalk"
                                    value={formData.squaretalk}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, squaretalk: e.target.value }))}
                                />
                            </div>
                            <div>
                                <Label htmlFor="octella">Octella ID</Label>
                                <Input
                                    id="octella"
                                    value={formData.octella}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, octella: e.target.value }))}
                                    placeholder="SIP line number (or employee ID)"
                                />
                            </div>
                            <div>
                                <Label htmlFor="ccc">MMD Call Center Connect Username</Label>
                                <Input
                                    id="ccc"
                                    value={formData.ccc}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, ccc: e.target.value }))}
                                    placeholder="Agent username (agent_id)"
                                />
                            </div>

                            <div>
                                <Label htmlFor="phone">Phone / Transfer number</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                                    placeholder="+441234567890"
                                />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    The number AI Caller transfers live calls to when this user is the receiving agent. Auto-fills in the campaign wizard.
                                </p>
                            </div>

                            {/* Password field only shown for new staff */}
                            {!staffId && (
                                <div>
                                    <Label htmlFor="password">Password{!staffId && " *"}</Label>
                                    <div className="relative">
                                        <Input
                                            id="password"
                                            type={showPassword ? "text" : "password"}
                                            value={formData.password}
                                            onChange={(e) =>
                                                setFormData((prev) => ({ ...prev, password: e.target.value }))
                                            }
                                            placeholder="Enter your password"
                                            required={!staffId}
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="h-4 w-4" />
                                            ) : (
                                                <Eye className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="grid grid-cols-1 gap-4">
                                <div className="">
                                    <div>
                                        <Label htmlFor="position">Position *</Label>
                                        <Select
                                            options={positions.map((position) => ({
                                                label: position.name,
                                                value: position._id
                                            }))}
                                            value={formData.position}
                                            onChange={(value) => handlePositionChange(value)}
                                            placeholder="Select position"
                                            searchable
                                        />
                                    </div>
                                    {formData.position.role === "admin" && (
                                        <div className="admin-privileges rounded-lg border border-yellow-300 bg-yellow-50 p-4 mt-2 shadow-sm">
                                            <div className="flex items-center mb-2">
                                                <svg
                                                    className="w-5 h-5 text-yellow-500 mr-2"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        fill="none"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 8v4m0 4h.01"
                                                    />
                                                </svg>
                                                <h3 className="font-semibold text-yellow-800 text-base">
                                                    Admin Position Privileges
                                                </h3>
                                            </div>
                                            <ul className="list-disc list-inside text-sm text-yellow-900 space-y-1 pl-2">
                                                <li>
                                                    Admins can{" "}
                                                    <span className="font-medium">import leads from Excel</span> unless
                                                    other rules prohibit it.
                                                </li>
                                                <li>
                                                    Admins can{" "}
                                                    <span className="font-medium">see, add and close tasks</span> for
                                                    all leads.
                                                </li>
                                                <li>
                                                    Admins can <span className="font-medium">assign any status</span> to
                                                    leads.
                                                </li>
                                                <li>
                                                    Admins can{" "}
                                                    <span className="font-medium">
                                                        see leads phone numbers and emails
                                                    </span>
                                                    .
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                    {positions.find((pos) => pos.value === formData.position)?.role ===
                                        "deskManager" && (
                                        <div className="admin-privileges rounded-lg border border-yellow-300 bg-yellow-50 p-4 mt-2 shadow-sm">
                                            <div className="flex items-center mb-2">
                                                <svg
                                                    className="w-5 h-5 text-yellow-500 mr-2"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        fill="none"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 8v4m0 4h.01"
                                                    />
                                                </svg>
                                                <h3 className="font-semibold text-yellow-800 text-base">
                                                    Manager Position Privileges
                                                </h3>
                                            </div>
                                            <ul className="list-disc list-inside text-sm text-yellow-900 space-y-1 pl-2">
                                                <li>
                                                    Managers can{" "}
                                                    <span className="font-medium">see, add and close tasks</span> for
                                                    all leads from available desks.
                                                </li>
                                                <li>
                                                    Managers can <span className="font-medium">assign any status</span>{" "}
                                                    to leads from available desks
                                                </li>
                                                <li>
                                                    Managers can{" "}
                                                    <span className="font-medium">
                                                        see leads phone numbers and emails
                                                    </span>{" "}
                                                    from available desks .
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                    {positions.find((pos) => pos.value === formData.position)?.role === "agent" && (
                                        <div className="admin-privileges rounded-lg border border-yellow-300 bg-yellow-50 p-4 mt-2 shadow-sm">
                                            <div className="flex items-center mb-2">
                                                <svg
                                                    className="w-5 h-5 text-yellow-500 mr-2"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="2"
                                                        fill="none"
                                                    />
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 8v4m0 4h.01"
                                                    />
                                                </svg>
                                                <h3 className="font-semibold text-yellow-800 text-base">
                                                    Agent Position Privileges
                                                </h3>
                                            </div>
                                            <ul className="list-disc list-inside text-sm text-yellow-900 space-y-1 pl-2">
                                                <li>
                                                    Agents can{" "}
                                                    <span className="font-medium">
                                                        see phone numbers and emails of allowed leads
                                                    </span>
                                                    .
                                                </li>
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Show current desk assignment for agents */}
                        {/* {staffId && isAgent && currentStaff?.agentDesk && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h4 className="font-medium mb-2">Current Desk Assignment</h4>
                                <div className="text-sm">
                                    <span className="text-gray-600">Assigned to:</span>
                                    <span className="ml-2 font-medium">{currentStaff.agentDesk.name}</span>
                                </div>
                            </div>
                        )} */}
                    </CardContent>
                </Card>

                {/* Permissions */}
                <Card>
                    <CardHeader>
                        <CardTitle>Permissions</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div>
                            <h4 className="font-medium mb-3">Lead Permissions</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {Object.keys(formData.permissions.leads).map((permission) => (
                                    <div key={permission} className="flex items-center space-x-2">
                                        <Switch
                                            id={`leads-${permission}`}
                                            checked={formData.permissions.leads[permission]}
                                            onCheckedChange={(checked) =>
                                                handlePermissionChange("leads", permission, checked)
                                            }
                                        />
                                        <Label htmlFor={`leads-${permission}`} className="text-sm capitalize">
                                            {permission.replace(/([A-Z])/g, " $1").trim()}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Separator />

                        {/* Lead Field Permissions */}
                        <div>
                            <h4 className="font-medium mb-3">Lead Field Edit Permissions</h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Control which lead fields this user can edit. This applies when the user has lead update
                                permissions.
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {Object.keys(formData.permissions.editLeadFields).map((field) => (
                                    <div key={field} className="flex items-center space-x-2">
                                        <Switch
                                            id={`editLeadFields-${field}`}
                                            checked={formData.permissions.editLeadFields[field]}
                                            onCheckedChange={(checked) =>
                                                handlePermissionChange("editLeadFields", field, checked)
                                            }
                                        />
                                        <Label htmlFor={`editLeadFields-${field}`} className="text-sm capitalize">
                                            {field.replace(/([A-Z])/g, " $1").trim()}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
                                <h4 className="font-medium text-blue-900 mb-2">Field Permission Notes</h4>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>
                                        • These permissions only apply when the user has &quot;Update&quot;permission
                                        for leads
                                    </li>
                                    <li>• Disabled fields will appear as read-only in the lead edit form</li>
                                </ul>
                            </div>
                        </div>

                        <Separator />

                        <div>
                            <h4 className="font-medium mb-3">Staff Permissions</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {Object.keys(formData.permissions.staff).map((permission) => (
                                    <div key={permission} className="flex items-center space-x-2">
                                        <Switch
                                            id={`staff-${permission}`}
                                            checked={formData.permissions.staff[permission]}
                                            onCheckedChange={(checked) =>
                                                handlePermissionChange("staff", permission, checked)
                                            }
                                        />
                                        <Label htmlFor={`staff-${permission}`} className="text-sm capitalize">
                                            {permission}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <Separator />
                        <div>
                            <h4 className="font-medium mb-3">Desk Permissions</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {Object.keys(formData.permissions.desks).map((permission) => (
                                    <div key={permission} className="flex items-center space-x-2">
                                        <Switch
                                            id={`desk-${permission}`}
                                            checked={formData.permissions.desks[permission]}
                                            onCheckedChange={(checked) =>
                                                handlePermissionChange("desks", permission, checked)
                                            }
                                        />
                                        <Label htmlFor={`staff-${permission}`} className="text-sm capitalize">
                                            {permission}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Separator />

                        {/* Column Permissions */}
                        <div>
                            <h4 className="font-medium mb-3">Grid leads column visibility</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {Object.keys(formData.permissions.gridLeadsColumns).map((column) => (
                                    <div key={column} className="flex items-center space-x-2">
                                        <Switch
                                            id={`columns-${column}`}
                                            checked={formData.permissions.gridLeadsColumns[column]}
                                            onCheckedChange={(checked) =>
                                                handlePermissionChange("gridLeadsColumns", column, checked)
                                            }
                                        />
                                        <Label htmlFor={`columns-${column}`} className="text-sm capitalize">
                                            {column.replace(/([A-Z])/g, " $1").trim()}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <Separator />

                        {/* VOIP Permissions */}
                        <div>
                            <h4 className="font-medium mb-3">VOIP Services</h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Control which VOIP services this user can access for voice calling functionality.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {Object.keys(formData.permissions.voip).map((service) => (
                                    <div key={service} className="flex items-center space-x-2">
                                        <Switch
                                            id={`voip-${service}`}
                                            checked={formData.permissions.voip[service]}
                                            onCheckedChange={(checked) =>
                                                handlePermissionChange("voip", service, checked)
                                            }
                                        />
                                        <Label htmlFor={`voip-${service}`} className="text-sm capitalize">
                                            {service === "ccc" ? "call center connect" : service}
                                        </Label>
                                    </div>
                                ))}
                            </div>
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
                                <h4 className="font-medium text-blue-900 mb-2">VOIP Permission Notes</h4>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>• These permissions control access to voice calling features</li>
                                    <li>• Users need corresponding VOIP IDs configured to use these services</li>
                                    <li>• System administrators must configure API keys in System Settings</li>
                                </ul>
                            </div>
                        </div>

                        <Separator />

                        {/* Games */}
                        <div>
                            <h4 className="font-medium mb-3">🎮 Games</h4>
                            <p className="text-sm text-gray-600 mb-4">
                                Per-user override for the in-app minigames. Defaults are inherited from the position.
                            </p>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <Label htmlFor="user-games-access" className="text-sm font-medium">Games access</Label>
                                        <p className="text-xs text-gray-500 mt-0.5">Allow this user to open the Games page</p>
                                    </div>
                                    <Switch
                                        id="user-games-access"
                                        checked={!!formData.permissions.games?.access}
                                        onCheckedChange={(checked) => handlePermissionChange("games", "access", checked)}
                                    />
                                </div>
                                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                        <Label htmlFor="user-games-mp" className="text-sm font-medium">Multiplayer</Label>
                                        <p className="text-xs text-gray-500 mt-0.5">Allow challenges with other agents</p>
                                    </div>
                                    <Switch
                                        id="user-games-mp"
                                        checked={formData.permissions.games?.multiplayer !== false}
                                        onCheckedChange={(checked) => handlePermissionChange("games", "multiplayer", checked)}
                                        disabled={!formData.permissions.games?.access}
                                    />
                                </div>
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <Label htmlFor="user-games-limit" className="text-sm font-medium">Daily play limit (minutes)</Label>
                                    <p className="text-xs text-gray-500 mt-0.5 mb-2">0 = unlimited.</p>
                                    <Input
                                        id="user-games-limit"
                                        type="number"
                                        min="0"
                                        value={formData.permissions.games?.dailyMinutesLimit ?? 0}
                                        onChange={(e) => handlePermissionChange("games", "dailyMinutesLimit", parseInt(e.target.value || "0", 10))}
                                        disabled={!formData.permissions.games?.access}
                                        className="max-w-[120px]"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* <Separator /> */}
                    </CardContent>
                </Card>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-3">
                        <Card>
                            <CardHeader>
                                <CardTitle>Leads Visibility</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="grid grid-cols-1 gap-4">
                                        <div>
                                            <Label htmlFor="position">View Leads </Label>
                                            <Select
                                                options={viewLeadsOptions.map((item) => ({ label: item, value: item }))}
                                                value={formData.viewLeadsScope}
                                                onChange={(value) => handleViewLeadsScopeChange(value)}
                                                placeholder="Select scope"
                                                searchable
                                            />
                                        </div>
                                    </div>
                                    {formData.viewLeadsScope === "Desk" && (
                                        <div>
                                            <Label>Allowed Desks</Label>
                                            <MultiSelect
                                                options={allDesks.map((desk) => ({
                                                    label: desk.name,
                                                    value: desk._id
                                                }))}
                                                selected={formData.viewLeadsAllowedDesks}
                                                onChange={(selected) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        viewLeadsAllowedDesks: selected
                                                    }))
                                                }
                                                placeholder="Select desks"
                                            />
                                            <p className="text-sm text-gray-500 mt-2">
                                                Selected: {formData.viewLeadsAllowedDesks.length} desks
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div>
                        <div className="mb-4">
                            <Label htmlFor="commentColor">Comment Color</Label>
                            <Input
                                id="commentColor"
                                type="color"
                                value={formData.commentColor}
                                onChange={(e) => setFormData((prev) => ({ ...prev, commentColor: e.target.value }))}
                            />
                        </div>

                        <div className="flex items-center space-x-6">
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="canFastLogin"
                                    checked={formData.canFastLogin}
                                    onCheckedChange={(checked) =>
                                        setFormData((prev) => ({ ...prev, canFastLogin: checked }))
                                    }
                                />
                                <Label htmlFor="canFastLogin">Can Fast Login</Label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex space-x-4">
                    <Button type="submit" className="bg-green-600 hover:bg-green-700">
                        {staffId ? "Update" : "Create"} Staff
                    </Button>
                    <Button type="button" variant="outline" onClick={handleBack}>
                        Cancel
                    </Button>
                </div>
            </form>


            {/* Update Password Modal */}
            <UpdatePasswordModal
                open={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSuccess={handlePasswordUpdateSuccess}
                staff={currentStaff}
            />
        </div>
    );
}
